import { SendOutlined } from "@ant-design/icons";
import { Link } from "@tanstack/react-router";
import { Card, Skeleton, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useSettingsList } from "@/modules/settings/hooks/useSettings";
import { holatVar } from "@/shared/charts";
import type { ReportDescriptorView } from "../../types";
import { reportTitle } from "../../utils/reportI18n";
import { ikonkaFoni } from "./hubFormat";

const { Text } = Typography;

/** `"0 19 * * *"` → `"19:00"`; murakkab naqsh → `null` (xom ko'rsatiladi). */
export function cronVaqti(cron: string): string | null {
	const mos = /^(\d{1,2}) (\d{1,2}) \* \* \*$/.exec(cron.trim());

	if (!mos) {
		return null;
	}

	return `${(mos[2] ?? "").padStart(2, "0")}:${(mos[1] ?? "").padStart(2, "0")}`;
}

export interface TelegramSummaryCardProps {
	descriptors: ReportDescriptorView[];
}

/**
 * Kunlik Telegram xulosasining holati.
 *
 * ⚠️ Chaqiruvchi `PermissionGate settings:read` ichida chizadi —
 * `GET /settings` shu ruxsatni talab qiladi va `useSettingsList` shu
 * komponentda, ya'ni ruxsatsiz rolda so'rov ketmaydi.
 *
 * ⚠️ `useSettingsList` sozlamalar sahifasi bilan BIR kesh kaliti
 * (`["settings"]`) — u yerda saqlangan qiymat bu kartada darhol
 * yangilanadi.
 */
export function TelegramSummaryCard({ descriptors }: TelegramSummaryCardProps) {
	const { t } = useTranslation("reports");
	const { data, isLoading, error } = useSettingsList();

	if (error) {
		return null;
	}

	const qiymat = (key: string): unknown => data?.find((s) => s.key === key)?.value;
	const yoqilgan = qiymat("report_summary_enabled") === true;
	const cron = String(qiymat("report_summary_cron") ?? "");
	const kind = String(qiymat("report_summary_kind") ?? "");
	const descriptor = descriptors.find((d) => d.kind === kind);
	const hisobot = descriptor ? reportTitle(t, descriptor) : kind;
	const vaqt = cronVaqti(cron);
	const rang = holatVar("kelgan");

	return (
		<Card size="small" data-telegram-summary>
			<div className="flex flex-col gap-2.5">
				<div className="flex items-center gap-2.5">
					<span
						aria-hidden="true"
						className="flex h-8 w-8 flex-none items-center justify-center rounded-md"
						style={{ backgroundColor: ikonkaFoni(rang), color: rang }}
					>
						<SendOutlined />
					</span>
					<span className="font-semibold text-sm">{t("hub.telegram.title")}</span>
				</div>

				{isLoading ? (
					<Skeleton active paragraph={{ rows: 1 }} title={false} />
				) : (
					<>
						<Text type="secondary" className="text-[13px]">
							{vaqt
								? t("hub.telegram.description", { time: vaqt, report: hisobot })
								: t("hub.telegram.descriptionCron", { cron, report: hisobot })}
						</Text>
						<div className="flex items-center justify-between">
							<Tag data-telegram-state={yoqilgan ? "on" : "off"} color={yoqilgan ? "blue" : "red"}>
								{yoqilgan ? t("hub.telegram.enabled") : t("hub.telegram.disabled")}
							</Tag>
							<Link to="/settings" className="font-medium text-[13px]">
								{t("hub.telegram.configure")}
							</Link>
						</div>
					</>
				)}
			</div>
		</Card>
	);
}
